/**
 * The home of the super admin panel (`admin.dashboard`, #4411): four readings in one answer, each its own section so
 * that one that fails does not hide the others: the platform counts (`stats`), the sync status (`syncs`), the job queues
 * (`queues`) and the last ten audit events (`recentActivity`). `syncs` carries only what the widgets paint: no error
 * text and no names of people.
 */

import { z } from 'zod/v4'

import { SyncSourceTypeSchema } from '../../contracts/imports/sync-history.contracts.js'
import { defineScreen, type ScreenData, type ScreenQuery } from '../define-screen.js'
import { freshnessSchema } from '../freshness.js'
import { screenSection } from '../section.js'
import { auditEventRowSchema } from './audit-common.js'

/** How many audit events «Actividad reciente» shows. */
export const DASHBOARD_RECENT_ACTIVITY_LIMIT = 10

/** The counts of the platform: `DashboardService.getSystemStats` without the sync block. */
export const dashboardStatsSchema = z.strictObject({
  tenants: z.strictObject({ total: z.number(), byStatus: z.record(z.string(), z.number()) }),
  users: z.strictObject({ total: z.number(), byRole: z.record(z.string(), z.number()) }),
  transactions: z.number(),
  storage: z.strictObject({ used: z.number(), limit: z.number() }),
})

export const dashboardActiveSyncSchema = z.strictObject({
  jobId: z.string(),
  sourceName: z.string(),
  type: SyncSourceTypeSchema,
  progress: z.number(),
})

export const dashboardFailedSyncSchema = z.strictObject({
  jobId: z.string(),
  sourceName: z.string(),
  canRetry: z.boolean(),
})

export const dashboardSyncsSchema = z.strictObject({
  summary: z.strictObject({ active: z.number(), completed: z.number(), failed: z.number() }),
  activeSyncs: z.array(dashboardActiveSyncSchema),
  failedSyncs: z.array(dashboardFailedSyncSchema),
  failedSyncsTotal: z.number(),
})

/** One job queue: `QueueStatistics`. */
export const queueStatisticsSchema = z.strictObject({
  queueName: z.string(),
  totalJobs: z.number(),
  pendingJobs: z.number(),
  processingJobs: z.number(),
  completedJobs: z.number(),
  failedJobs: z.number(),
  avgProcessingTime: z.number(),
})

export const adminDashboardScreen = defineScreen({
  id: 'admin.dashboard',
  path: '/api/screens/admin/dashboard',
  access: 'super_admin',
  query: z.strictObject({}),
  response: z.strictObject({
    stats: screenSection(dashboardStatsSchema),
    syncs: screenSection(dashboardSyncsSchema),
    queues: screenSection(z.array(queueStatisticsSchema)),
    recentActivity: screenSection(
      z.array(auditEventRowSchema).max(DASHBOARD_RECENT_ACTIVITY_LIMIT),
    ),
    freshness: freshnessSchema,
  }),
  dependsOn: ['admin.dashboard'],
  budget: { requests: 1, statements: 40, bytes: 60_000, p95Ms: 800 },
  summary: 'System home: platform counts, sync status, job queues and the last ten audit events',
})
export type AdminDashboardQuery = ScreenQuery<typeof adminDashboardScreen>
export type AdminDashboardData = ScreenData<typeof adminDashboardScreen>
export type DashboardStats = z.infer<typeof dashboardStatsSchema>
export type DashboardSyncs = z.infer<typeof dashboardSyncsSchema>
